/**
 * Sanitize utility to prevent XSS vulnerabilities
 * Removes potentially dangerous HTML/JavaScript while preserving safe content
 * SHARED PRODUCT CODE - Used by all applications
 */

const DANGEROUS_TAGS = [
    'script',
    'iframe',
    'embed',
    'object',
    'form',
    'input',
    'button',
    'textarea',
    'select',
    'style',
    'link',
    'meta',
    'base',
]

const DANGEROUS_ATTRS = [
    'onclick',
    'onload',
    'onerror',
    'onmouseover',
    'onmouseout',
    'onmouseenter',
    'onmouseleave',
    'onchange',
    'onsubmit',
    'ondblclick',
    'onkeydown',
    'onkeyup',
    'onblur',
    'onfocus',
    'onwheel',
    'onscroll',
    'ontouchstart',
    'ontouchend',
    'ontouchmove',
    'onpointerdown',
    'onpointerup',
    'onpointermove',
    'dangerouslySetInnerHTML',
    'jsaction',
    'ondevicemotion',
    'ondeviceorientation',
    'oncontextmenu',
    'ondrag',
    'ondrop',
]

const ALLOWED_TAGS = [
    'p',
    'br',
    'span',
    'div',
    'a',
    'strong',
    'em',
    'u',
    'i',
    'b',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'code',
    'pre',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'img',
    'video',
    'audio',
    'source',
]

const ALLOWED_ATTRS_BY_TAG = {
    a: ['href', 'title', 'target', 'rel'],
    img: ['src', 'alt', 'title', 'width', 'height'],
    video: ['src', 'width', 'height', 'controls', 'autoplay'],
    audio: ['src', 'controls', 'autoplay'],
    source: ['src', 'type'],
    '*': ['class', 'id', 'data-*'],
}

// href/src are allowed as attributes, but their VALUE must still be checked -
// otherwise `<a href="javascript:...">` or `<a href="data:text/html,...">`
// sail straight through the tag/attribute allow-list above. Browsers strip
// tab/newline/CR before resolving a URL's scheme, so that must be stripped
// here too or "java\tscript:" bypasses a plain prefix check.
const URL_VALUE_ATTRS = ['href', 'src']
const isSafeAttributeUrlValue = (value, tagName, attrName) => {
    if (typeof value !== 'string') return true
    const normalized = value
        .replace(/[\t\n\r]/g, '')
        .trim()
        .toLowerCase()
    if (!normalized) return true
    if (/^(javascript|vbscript|file|filesystem|blob):/.test(normalized)) {
        return false
    }
    if (normalized.startsWith('data:')) {
        return (
            tagName === 'img' &&
            attrName === 'src' &&
            /^data:image\/(gif|jpe?g|png|webp);base64,/.test(normalized)
        )
    }
    return attrName === 'href'
        ? /^(https?:|\/|#|\?)/.test(normalized)
        : /^(https?:|\/|#|\?)/.test(normalized)
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - Raw HTML content
 * @param {boolean} allowImages - Whether to allow image tags (default: true)
 * @returns {string} Sanitized HTML
 */
export const sanitizeHTML = (html, allowImages = true) => {
    if (!html || typeof html !== 'string') {
        return ''
    }

    // Create a temporary DOM element to parse HTML
    const temp = document.createElement('div')
    temp.innerHTML = html

    // Recursively clean nodes
    const cleanNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase()

            // Remove dangerous tags entirely
            if (DANGEROUS_TAGS.includes(tagName)) {
                return null
            }

            // Remove images if not allowed
            if (tagName === 'img' && !allowImages) {
                return null
            }

            // Keep safe tags
            if (ALLOWED_TAGS.includes(tagName)) {
                // Remove all attributes
                const attrs = node.attributes
                for (let i = attrs.length - 1; i >= 0; i--) {
                    const attr = attrs[i]
                    const attrName = attr.name.toLowerCase()

                    // Remove dangerous attributes
                    if (DANGEROUS_ATTRS.includes(attrName)) {
                        node.removeAttribute(attr.name)
                        continue
                    }

                    // Only allow specific attributes for specific tags
                    const allowedAttrs = ALLOWED_ATTRS_BY_TAG[tagName] || []
                    const allowedGlobal = ALLOWED_ATTRS_BY_TAG['*'] || []

                    const isAllowed =
                        allowedAttrs.includes(attrName) ||
                        allowedGlobal.some((pattern) => {
                            if (pattern.endsWith('-*')) {
                                return attrName.startsWith(pattern.slice(0, -1))
                            }
                            return pattern === attrName
                        })

                    const isSafeValue =
                        !URL_VALUE_ATTRS.includes(attrName) ||
                        isSafeAttributeUrlValue(attr.value, tagName, attrName)

                    if (!isAllowed || !isSafeValue) {
                        node.removeAttribute(attr.name)
                    }

                    if (tagName === 'a' && node.hasAttribute('target')) {
                        node.setAttribute('target', '_blank')
                        node.setAttribute('rel', 'noopener noreferrer')
                    }
                }

                // Recursively clean child nodes
                const children = Array.from(node.childNodes)
                for (const child of children) {
                    const cleanedChild = cleanNode(child)
                    if (cleanedChild === null) {
                        node.removeChild(child)
                    }
                }

                return node
            }

            // Remove unknown tags but keep their content
            const fragment = document.createDocumentFragment()
            const children = Array.from(node.childNodes)
            for (const child of children) {
                const cleanedChild = cleanNode(child)
                if (cleanedChild !== null) {
                    fragment.appendChild(cleanedChild)
                }
            }
            return fragment
        }

        return node
    }

    // Clean the container's children. The container itself is not user HTML;
    // treating it as an unknown element would return a DocumentFragment without
    // an innerHTML property and silently discard otherwise safe content.
    for (const child of Array.from(temp.childNodes)) {
        const cleanedChild = cleanNode(child)
        if (cleanedChild === null) {
            temp.removeChild(child)
        }
    }

    return temp.innerHTML
}

/**
 * Create markup object for dangerouslySetInnerHTML with sanitized content
 * @param {string} html - Raw HTML content
 * @param {boolean} allowImages - Whether to allow image tags (default: true)
 * @returns {object} Object with __html property containing sanitized HTML
 */
export const createSafeMarkup = (html, allowImages = true) => ({
    __html: sanitizeHTML(html, allowImages),
})

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - Raw text to escape
 * @returns {string} Escaped text safe for HTML display
 */
export const escapeHTML = (text) => {
    if (!text || typeof text !== 'string') {
        return ''
    }

    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
    }

    return text.replace(/[&<>"']/g, (char) => map[char])
}

export default {
    sanitizeHTML,
    createSafeMarkup,
    escapeHTML,
}
